/**
 * Google Analytics 4 Integration
 * Tracks page views, events, and user behavior
 */

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.measurementId = null;
  }

  /**
   * Initialize Google Analytics
   * @param {string} measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
   */
  initialize(measurementId) {
    if (!measurementId) {
      console.warn('GA4: No measurement ID provided');
      return;
    }

    if (this.isInitialized) {
      console.warn('GA4: Already initialized');
      return;
    }

    this.measurementId = measurementId;

    // Load GA4 script dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false, // We'll send manually for SPA routing
    });

    this.isInitialized = true;
  }

  /**
   * Track page view
   * @param {string} path - Page path
   * @param {string} title - Page title
   */
  pageView(path, title) {
    if (!this.isInitialized) return;

    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }

  /**
   * Set user ID for tracking
   * @param {string} userId - User ID
   */
  setUserId(userId) {
    if (!this.isInitialized) return;

    window.gtag('config', this.measurementId, {
      user_id: userId,
    });
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties
   */
  setUserProperties(properties) {
    if (!this.isInitialized) return;

    window.gtag('set', 'user_properties', properties);
  }

  /**
   * Track custom event
   * @param {string} eventName - Event name
   * @param {Object} params - Event parameters
   */
  event(eventName, params = {}) {
    if (!this.isInitialized) return;

    window.gtag('event', eventName, params);
  }

  // Convenience methods for common events
  
  /**
   * Track user login
   * @param {string} method - Login method (email, google, etc)
   */
  trackLogin(method = 'email') {
    this.event('login', { method });
  }

  /**
   * Track user signup
   * @param {string} method - Signup method
   */
  trackSignup(method = 'email') {
    this.event('sign_up', { method });
  }

  /**
   * Track document upload
   * @param {string} documentType - Type of document
   */
  trackDocumentUpload(documentType) {
    this.event('document_upload', {
      document_type: documentType,
    });
  }

  /**
   * Track a saved training plan
   * @param {string} source - document | url | chat
   */
  trackPlanCreated(source) {
    this.event('plan_created', { source });
  }

  /**
   * Track a game published for learners
   */
  trackGamePublished() {
    this.event('game_published');
  }

  /**
   * Track invoice generation
   * @param {number} amount - Invoice amount
   */
  trackInvoiceGenerated(amount) {
    this.event('invoice_generated', {
      value: amount,
      currency: 'USD',
    });
  }

  /**
   * Track search
   * @param {string} searchTerm - Search term
   */
  trackSearch(searchTerm) {
    this.event('search', {
      search_term: searchTerm,
    });
  }

  /**
   * Track feature usage
   * @param {string} feature - Feature name
   * @param {Object} metadata - Additional data
   */
  trackFeatureUsage(feature, metadata = {}) {
    this.event('feature_used', {
      feature_name: feature,
      ...metadata,
    });
  }

  /**
   * Track button click
   * @param {string} buttonName - Button identifier
   * @param {string} location - Where the button is located
   */
  trackButtonClick(buttonName, location) {
    this.event('button_click', {
      button_name: buttonName,
      location,
    });
  }

  /**
   * Track error (for analytics, not debugging)
   * @param {string} errorType - Type of error
   * @param {boolean} fatal - Is error fatal
   */
  trackError(errorType, fatal = false) {
    this.event('exception', {
      description: errorType,
      fatal,
    });
  }

  /**
   * Track timing (performance)
   * @param {string} name - Timing name
   * @param {number} value - Time in milliseconds
   * @param {string} category - Category
   */
  trackTiming(name, value, category = 'general') {
    this.event('timing_complete', {
      name,
      value,
      event_category: category,
    });
  }

  /**
   * Enable debug mode
   */
  enableDebugMode() {
    if (!this.isInitialized) return;
    
    window.gtag('config', this.measurementId, {
      debug_mode: true,
    });
    console.log('🔍 GA4 Debug mode enabled');
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;
